import React, { useEffect, useRef } from 'react';

// Public site key — safe to expose in the frontend. Set at build time via
// REACT_APP_TURNSTILE_SITE_KEY (Cloudflare Pages -> Variables). If absent,
// the widget renders nothing and the server skips verification (dev/preview).
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
function loadTurnstile() {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('turnstile load failed'));
    document.head.appendChild(el);
  });
  return scriptPromise;
}

// Non-invasive Turnstile: appearance "interaction-only" stays invisible unless
// a challenge is actually required. Emits the token via onToken().
export default function TurnstileWidget({ onToken, nonce = 0 }) {
  const containerRef = useRef(null);
  const idRef = useRef(null);

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return;
        try { if (idRef.current != null) window.turnstile.remove(idRef.current); } catch (e) { /* noop */ }
        containerRef.current.innerHTML = '';
        idRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          appearance: 'interaction-only',
          'refresh-expired': 'auto',
          callback: (token) => onToken(token),
          'error-callback': () => onToken(''),
          'expired-callback': () => onToken(''),
        });
      })
      .catch(() => { /* network issue: server still decides, do not block UI */ });

    return () => {
      cancelled = true;
      try { if (idRef.current != null && window.turnstile) window.turnstile.remove(idRef.current); } catch (e) { /* noop */ }
      idRef.current = null;
    };
  }, [onToken, nonce]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} className="cf-turnstile" />;
}

export const TURNSTILE_ENABLED = Boolean(SITE_KEY);
